import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Col, Form, FormGroup } from 'react-bootstrap';
import PropTypes from 'prop-types';
import DeviceInfoExtended from 'containers/request/Initial/Steps/Vendor/DeviceInfoExtended';
import DeviceInfoDates from 'containers/request/Initial/Steps/Vendor/DeviceInfoDates';
import DeviceInfoSubmitter from 'containers/request/Initial/Steps/Vendor/DeviceInfoSubmitter';
import { Bubble, LoaderButton, ContinueLabel, Divider } from 'ctia-ui';
import FormElement from 'components/FormElement';
import { getDeviceTypeList, getDeviceTitle } from 'helpers/DeviceTypeHelper';
import { getModelBands } from 'helpers/Bands';
import { notifyMessages } from 'helpers/NotifyHelper';
import { hasStringValue, isInitial } from 'helpers/RequestHelper';
import { emptyMsg } from 'helpers/FormHelper';
import { checkDPSW } from 'helpers/DateHelper';
import {
  handleInputChange,
  handleCheckboxGroupChange,
  updateDeviceModel,
  updateRequest,
  setValidationStatus
} from 'actions/request';
import { submitWizardStep } from 'actions/wizard';
import { startPreloader, finishPreloader } from 'actions/preloader';
import { showNotice } from 'actions/notifier';
import { checkExistedModel } from 'actions/model';

class DeviceInfo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modelNumberValid: true,
      isExistedRequest: false,
      hasFccId: false,
      hasHwVersion: false,
      hasSwVersion: false,
      hasDescChange: false
    };
  }

  componentDidMount() {
    const { request } = this.props;
    const isExistedRequest = _.has(request, 'id') && request.id > 0;
    this.setState({
      isExistedRequest,
      hasFccId: this.hasFccId(),
      hasHwVersion: this.hasHwVersion(),
      hasSwVersion: this.hasSwVersion(),
      hasDescChange: this.hasDescChange()
    });
  }

  componentDidUpdate(prevProps) {
    const { request } = this.props;
    if (
      request.fccId !== prevProps.request.fccId ||
      request.hwVersion !== prevProps.request.hwVersion ||
      request.swVersion !== prevProps.request.swVersion ||
      request.descriptionOfChange !== prevProps.request.descriptionOfChange ||
      request.modelName !== prevProps.request.modelName ||
      Number(request.deviceType) !== Number(prevProps.request.deviceType)
    ) {
      const isValid =
        this.validateFccId() === true &&
        this.validateHwVersion() === true &&
        this.validateSwVersion() === true &&
        this.validateDescChange() === true &&
        this.validateBasicFields() === true;
      if (request.isValid !== isValid) {
        this.props.setValidationStatus(isValid);
      }
    }
  }

  hasFccId() {
    return hasStringValue(this.props.request, 'fccId');
  }

  hasHwVersion() {
    return hasStringValue(this.props.request, 'hwVersion');
  }

  hasSwVersion() {
    return hasStringValue(this.props.request, 'swVersion');
  }

  hasDescChange() {
    return hasStringValue(this.props.request, 'descriptionOfChange');
  }

  hasModelNumber() {
    return hasStringValue(this.props.request, 'modelName');
  }

  validateFccId() {
    // field should be valid if it's empty
    if (!this.state.hasFccId) return true;
    return this.hasFccId();
  }

  validateHwVersion() {
    // field should be valid if it's empty
    if (!this.state.hasHwVersion) return true;
    return this.hasHwVersion();
  }

  validateSwVersion() {
    // field should be valid if it's empty
    if (!this.state.hasSwVersion) return true;
    return this.hasSwVersion();
  }

  validateDescChange() {
    // field should be valid if it's empty
    if (!this.state.hasDescChange) return true;
    return this.hasDescChange();
  }

  validateBasicFields() {
    const { request } = this.props;
    return hasStringValue(request, 'modelName') && request.deviceType > 0;
  }

  validateDpSw() {
    const { request, user } = this.props;
    if (!user.ctiaStaff) return true;

    // don't validate for staff if no hw/sw entered by lab yet
    const hasHwVersion = hasStringValue(request, 'hwVersion');
    const hasSwVersion = hasStringValue(request, 'swVersion');
    if (!hasHwVersion && !hasSwVersion) return true;

    // don't validate for already approved devices
    // before date we rolled out DPSW feature (10/29/2018)
    if (request.isApproved && !checkDPSW(request.completeDate)) return true;

    return request.isCBSDDPdevice
      ? hasStringValue(request, 'dpSwVersion')
      : true;
  }

  validate() {
    if (!this.props.user.canEditRequest) return true;
    if (this.state.isExistedRequest) {
      return (
        this.validateBasicFields() &&
        this.validateFccId() &&
        this.validateHwVersion() &&
        this.validateSwVersion() &&
        this.validateDescChange() &&
        this.validateDpSw()
      );
    }

    // for new submission need to check model name and device type only
    return this.validateBasicFields();
  }

  onSaveClick = () => {
    this.props.startPreloader();
    this.updateModel()
      .catch(this.modelNameErrorCallback)
      .then(this.updateRequestFields)
      .catch(this.requestUpdateErrorCallback)
      .then(this.updateCallback);
  };

  updateModel = () => {
    const { request, updateDeviceModel } = this.props;
    const { model, deviceType, modelName } = request;

    // prepare device model data to change
    const deviceInfo = {
      id: model.id,
      modelnumber: _.trim(modelName),
      devicetypeid: deviceType,
      devicemodelbands: getModelBands(request.bands),
      fccid: _.trim(request.fccId)
    };

    return updateDeviceModel(deviceInfo.id, deviceInfo);
  };

  modelNameErrorCallback = () => {
    this.props.showNotice(notifyMessages.modelnameAlreadyExists);
  };

  requestUpdateErrorCallback = () => {
    this.props.showNotice(notifyMessages.cantUpdateRequest);
  };

  updateCallback = () => {
    const { finishPreloader, showNotice } = this.props;
    finishPreloader();
    showNotice(notifyMessages.requestUpdatedSuccessfully);
  };

  submitStep = () => {
    const { startPreloader, checkExistedModel, request } = this.props;
    startPreloader();
    checkExistedModel(request.modelName).then(this.checkModelCallback);
  };

  checkModelCallback = res => {
    const { finishPreloader, showNotice } = this.props;
    const isModelExists = res.data.meta.TOTALRECORDS > 0;
    if (isModelExists) {
      // show error message, show erroring field
      showNotice(notifyMessages.modelnameAlreadyExists);
      this.setState({ modelNumberValid: false });
    } else {
      const { wizard, submitWizardStep } = this.props;
      submitWizardStep(wizard);
    }
    finishPreloader();
  };

  onModelChange = event => {
    this.setState({ modelNumberValid: true });
    this.props.handleInputChange(event);
  };

  updateRequestFields = () => {
    const { request, updateRequest } = this.props;
    const {
      id,
      hwVersion,
      swVersion,
      descriptionOfChange,
      dpSwVersion
    } = request;
    const requestInfo = {
      id,
      hwVersion: _.trim(hwVersion),
      swVersion: _.trim(swVersion),
      dpSwVersion: _.trim(dpSwVersion),
      descriptionOfChange: _.trim(descriptionOfChange)
    };

    // update hw/sw for the request
    return updateRequest(requestInfo);
  };

  isValidationRequired = () => {
    return this.props.user.canEditRequest;
  };

  isModelFieldValid = () => {
    const isValidModel = this.state.modelNumberValid;
    if (!isValidModel) return false;
    if (this.state.isExistedRequest) return this.hasModelNumber();
    return true;
  };

  validateModelNumber = () => {
    if (!this.isValidationRequired()) return null;
    return this.isModelFieldValid() ? null : 'error';
  };

  getModelValidationMessage = () => {
    if (!this.state.modelNumberValid)
      return notifyMessages.modelnameAlreadyExists.text;
    if (!this.hasModelNumber()) return emptyMsg;
    return null;
  };

  validateDeviceType = () => {
    if (!this.isValidationRequired()) return null;
    if (this.state.isExistedRequest) {
      return this.props.request.deviceType > 0 ? null : 'error';
    }
    return null;
  };

  render() {
    if (!this.props.show) return null;

    const {
      request,
      canSave,
      canSubmit,
      handleInputChange,
      handleCheckboxGroupChange,
      user
    } = this.props;

    const {
      modelName,
      deviceType,
      bands,
      isLoading,
      id,
      vendor,
      type
    } = request;
    const hasRequestId = !_.isUndefined(id);
    const canEditModelName =
      (hasRequestId && user.isStaff) || (!hasRequestId && user.isVendor);
    const isEditable = user.canEditRequest;
    const vendorName = hasRequestId ? vendor : user.company;
    const canEditDeviceType = isEditable && isInitial(type);
    const deviceTitle = canEditDeviceType
      ? deviceType
      : getDeviceTitle(deviceType);

    return (
      <div>
        <Bubble show={!canSave}>
          Please provide the following information about the device.
        </Bubble>
        <Bubble show={canSave}>
          Below is the device information entered for this request.
        </Bubble>
        <Form horizontal>
          <FormElement
            id="manufTitle"
            label="Manufacturer"
            value={vendorName}
            editable={isEditable}
          />
          <FormElement
            type={canEditModelName && isEditable ? 'text' : ''}
            id="modelName"
            label="Model Name/Number"
            value={modelName}
            onChange={this.onModelChange}
            placeholder="Please enter the device model name or number"
            editable={isEditable}
            validationState={this.validateModelNumber()}
            validationMessage={this.getModelValidationMessage()}
          />
          <DeviceInfoSubmitter />
          <DeviceInfoDates />
          <FormElement
            type={canEditDeviceType ? 'select' : ''}
            id="deviceType"
            label="Device Type"
            value={deviceTitle}
            onChange={handleInputChange}
            options={getDeviceTypeList()}
            editable={isEditable}
            validationState={this.validateDeviceType()}
            validationMessage={emptyMsg}
          />
          <FormElement
            type="checkboxList"
            id="bands"
            label="Bands"
            value={bands}
            onChange={handleCheckboxGroupChange.bind(this, bands)}
            list={bands}
            editable={isEditable}
          />
          {/* Shows extended fields like FCC ID and HW/SW Versions */}
          <DeviceInfoExtended />
          <Divider />
          <FormGroup>
            <Col sm={4} md={3} />
            <Col sm={4} md={3}>
              <LoaderButton
                disabled={!this.validate()}
                bsStyle="default"
                text="Save changes"
                show={isEditable && request.id > 0}
                isLoading={isLoading}
                onClick={this.onSaveClick}
              />
            </Col>
            <Col sm={4} md={6} className="text-right">
              <LoaderButton
                disabled={!this.validate()}
                bsStyle="info"
                text={<ContinueLabel />}
                show={canSubmit}
                isLoading={isLoading}
                onClick={this.submitStep}
              />
            </Col>
          </FormGroup>
        </Form>
      </div>
    );
  }
}

DeviceInfo.propTypes = {
  show: PropTypes.bool,
  isEditable: PropTypes.bool,
  canSave: PropTypes.bool,
  canSubmit: PropTypes.bool
};

DeviceInfo.defaultProps = {
  show: true,
  canSave: false,
  canSubmit: false
};

function mapStateToProps({ request, wizard, user }) {
  return { request, wizard, user };
}

export default connect(
  mapStateToProps,
  {
    handleInputChange,
    handleCheckboxGroupChange,
    updateDeviceModel,
    submitWizardStep,
    startPreloader,
    finishPreloader,
    showNotice,
    checkExistedModel,
    updateRequest,
    setValidationStatus
  }
)(DeviceInfo);



// WEBPACK FOOTER //
// ./src/containers/request/Initial/Steps/Vendor/DeviceInfo.js